import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePosts } from '@/lib/cms';
import type { Post } from '@/lib/cms-types';
import { formatDate, readingTime } from '@/lib/utils';

const CATEGORIES = [
  'all',
  'Technology',
  'AI',
  'Software Engineering',
  'Cloud',
  'Startups',
  'Cybersecurity',
  'Programming',
  'Mobile',
  'Web',
  'Digital Transformation',
];

export function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') ?? 'all';
  const page = Number(searchParams.get('page') ?? '1');
  const PER_PAGE = 12;

  const { data: posts = [], isLoading } = usePosts();

  const filtered = useMemo(() => {
    const list = category === 'all' ? posts : posts.filter((p) => p.category === category);
    const start = (page - 1) * PER_PAGE;
    return { items: list.slice(start, start + PER_PAGE), total: list.length, totalPages: Math.max(1, Math.ceil(list.length / PER_PAGE)) };
  }, [posts, category, page]);

  function setCategory(c: string) {
    if (c === 'all') searchParams.delete('category');
    else searchParams.set('category', c);
    searchParams.delete('page');
    setSearchParams(searchParams, { replace: true });
  }

  function setPage(p: number) {
    if (p === 1) searchParams.delete('page');
    else searchParams.set('page', String(p));
    setSearchParams(searchParams, { replace: true });
  }

  return (
    <>
      <Meta
        title="Blog"
        description="Insights from the Vonlof team — notes on engineering, design, and building in Africa."
        path="/blog"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Insights</p>
          </FadeIn>
          <TextSplit
            text="Notes from the team."
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
          <FadeIn delay={0.3}>
            <p className="mt-6 max-w-2xl text-lg text-fg-muted">
              Notes on engineering, design, and building software in Africa — written by the people doing the work.
            </p>
          </FadeIn>

          {/* CATEGORIES */}
          <FadeIn delay={0.2}>
            <div
              className="-mx-4 mt-10 flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide scroll-x-mask sm:mx-0 sm:mt-12 sm:flex-wrap sm:overflow-visible sm:px-0 sm:[mask-image:none]"
              role="tablist"
              aria-label="Filter by category"
            >
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={category === c}
                  onClick={() => setCategory(c)}
                  className={
                    category === c
                      ? 'rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1.5 text-xs font-medium text-gold-500'
                      : 'rounded-full border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg'
                  }
                >
                  {c === 'all' ? 'All' : c}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* FEATURED (first post) */}
          {!isLoading && filtered.items.length > 0 && page === 1 && category === 'all' && (
            <FeaturedPost post={filtered.items[0]!} />
          )}

          {/* GRID */}
          {isLoading ? (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="h-full overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardHeader>
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="mt-2 h-5 w-3/4" />
                    <Skeleton className="mt-2 h-4 w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : filtered.items.length === 0 || (page === 1 && category === 'all' && filtered.items.length === 1) ? (
            <Card className="mt-12 p-12 text-center text-fg-subtle">
              No posts in this category yet.
            </Card>
          ) : (
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {(page === 1 && category === 'all' ? filtered.items.slice(1) : filtered.items).map((p) => (
                <StaggerItem key={p.id}>
                  <BlogCard post={p} />
                </StaggerItem>
              ))}
            </Stagger>
          )}

          {/* PAGINATION */}
          {filtered.totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-fg-muted">
                Page {page} of {filtered.totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= filtered.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </MaskReveal>
      </main>
    </>
  );
}

function BlogCard({ post }: { post: Post }) {
  const readMin = post.reading_time_min ?? readingTime(post.body_markdown);
  return (
    <Link to={`/blog/${post.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
        <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
          {post.hero_image_url ? (
            <img
              src={post.hero_image_url}
              alt={`${post.title} hero`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
              {post.category}
            </div>
          )}
        </div>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="gold">{post.category}</Badge>
            {post.published_at && <span className="text-xs text-fg-subtle">{formatDate(post.published_at)}</span>}
            <span className="text-xs text-fg-subtle">· {readMin} min read</span>
          </div>
          <CardTitle className="mt-3 line-clamp-2">{post.title}</CardTitle>
          {post.excerpt && <CardDescription className="line-clamp-2">{post.excerpt}</CardDescription>}
          {post.author && (
            <div className="mt-3 flex items-center gap-2 text-xs text-fg-subtle">
              {post.author.name}
            </div>
          )}
          <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold-500">
            Read post
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </CardHeader>
      </Card>
    </Link>
  );
}

function FeaturedPost({ post }: { post: Post }) {
  return (
    <FadeIn delay={0.25}>
      <Link to={`/blog/${post.slug}`} className="group mt-12 block">
        <div className="grid gap-8 rounded-2xl border border-border bg-bg-elevated/40 p-6 transition-colors hover:border-gold-500/40 md:grid-cols-2 md:p-8">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-gradient-to-br from-gold-500/20 to-bg-elevated md:aspect-auto md:h-full">
            {post.hero_image_url ? (
              <img
                src={post.hero_image_url}
                alt={`${post.title} hero`}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
                {post.category}
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2">
              <Badge variant="gold">Featured</Badge>
              <span className="text-xs text-fg-subtle">{formatDate(post.published_at!)}</span>
            </div>
            <h2 className="display-serif mt-3 text-3xl text-fg md:text-4xl">{post.title}</h2>
            <p className="mt-4 text-fg-muted">{post.excerpt}</p>
            <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-gold-500">
              Read the post <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </FadeIn>
  );
}